import { useSelector } from "react-redux";
import React, { useState, useEffect, useCallback } from "react";
import AdminShell from "./AdminShell";
import { dialog } from "../Common/TreasuryDialog";

const API = process.env.REACT_APP_API_URL || "";

const statusColors = {
  pending: { bg: "#fef9c3", color: "#854d0e" },
  approved: { bg: "#dcfce7", color: "#166534" },
  rejected: { bg: "#fee2e2", color: "#991b1b" },
};

const WithdrawalManagement = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const authHeader = { Authorization: `Bearer ${token}` };

  const loadWithdrawals = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/admin/withdrawals?status=${filter}`, { headers: authHeader })
      .then((r) => r.json())
      .then((data) => setWithdrawals(data.withdrawals || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter, token]);

  useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  const handleApprove = async (w) => {
    const confirmed = await dialog.confirm(
      "Approve Withdrawal",
      `Approve SAR ${w.amount.toLocaleString()} withdrawal for ${w.userId?.name}?\n\nIBAN: ${w.iban}\nBank: ${w.bankName}`
    );
    if (!confirmed) return;
    setActionLoading(w._id);
    try {
      const res = await fetch(`${API}/api/admin/withdrawals/approve`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ withdrawalId: w._id }),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success("Approved", data.msg);
        loadWithdrawals();
      } else {
        dialog.error("Error", data.msg);
      }
    } catch {
      dialog.error("Error", "Network error.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (w) => {
    const confirmed = await dialog.confirm(
      "Reject Withdrawal",
      `Reject SAR ${w.amount.toLocaleString()} withdrawal for ${w.userId?.name}?\n\nThe amount will be refunded to their wallet balance.`
    );
    if (!confirmed) return;
    setActionLoading(w._id);
    try {
      const res = await fetch(`${API}/api/admin/withdrawals/reject`, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body: JSON.stringify({ withdrawalId: w._id }),
      });
      const data = await res.json();
      if (data.status === 1) {
        dialog.success("Rejected", data.msg);
        loadWithdrawals();
      } else {
        dialog.error("Error", data.msg);
      }
    } catch {
      dialog.error("Error", "Network error.");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <AdminShell pageTitle="Withdrawal Management">
      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: "2px solid #e5e7eb" }}>
        {["pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              background: "none",
              border: "none",
              padding: "10px 24px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              color: filter === s ? "#c9a84c" : "#6b7280",
              borderBottom: filter === s ? "2px solid #c9a84c" : "2px solid transparent",
              marginBottom: -2,
              transition: "all 0.18s",
              textTransform: "capitalize",
            }}
          >
            {s}
          </button>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
        {loading ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0" }}>Loading…</div>
        ) : withdrawals.length === 0 ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
            No {filter} withdrawal requests
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Investor</th>
                <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Amount (SAR)</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>IBAN</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Bank</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Status</th>
                {filter === "pending" && (
                  <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {withdrawals.map((w) => {
                const sc = statusColors[w.status] || {};
                return (
                  <tr key={w._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "12px 12px", color: "#374151" }}>
                      {new Date(w.createdAt).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}
                    </td>
                    <td style={{ padding: "12px 12px" }}>
                      <div style={{ fontWeight: 600, color: "#111827" }}>{w.userId?.name || w.accountHolderName}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>{w.userId?.email}</div>
                    </td>
                    <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 700, color: "#c9a84c", fontSize: 15 }}>
                      {Number(w.amount).toLocaleString("en-SA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ padding: "12px 12px", color: "#374151", fontFamily: "monospace", fontSize: 12 }}>
                      {w.iban}
                    </td>
                    <td style={{ padding: "12px 12px", color: "#374151" }}>{w.bankName}</td>
                    <td style={{ padding: "12px 12px" }}>
                      <span
                        style={{
                          background: sc.bg,
                          color: sc.color,
                          padding: "3px 10px",
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {w.status}
                      </span>
                      {w.adminNote && (
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{w.adminNote}</div>
                      )}
                    </td>
                    {filter === "pending" && (
                      <td style={{ padding: "12px 12px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                          <button
                            onClick={() => handleApprove(w)}
                            disabled={actionLoading === w._id}
                            style={{
                              padding: "6px 14px",
                              background: "#166534",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                              opacity: actionLoading === w._id ? 0.5 : 1,
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(w)}
                            disabled={actionLoading === w._id}
                            style={{
                              padding: "6px 14px",
                              background: "#dc2626",
                              color: "#fff",
                              border: "none",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                              opacity: actionLoading === w._id ? 0.5 : 1,
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
};

export default WithdrawalManagement;
